import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import PageHead from "@/components/PageHead";

export default function About() {
  return (
    <>
      <PageHead
        title="About ChronoVaults | Future Messaging"
        description="ChronoVaults was built for messages that deserve time, intention and meaning — future video and personal messages, delivered at the moment you choose."
        canonical="https://chronovaults.co.uk/about"
      />
    <div className="relative" data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-20 pb-16">
          <div className="animate-fade-in">
            <p className="label-mono text-terracotta" data-testid="about-eyebrow">
              ABOUT · CHRONOVAULTS
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-bone mt-8"
              data-testid="about-heading"
            >
              About <em className="text-terracotta not-italic font-light">ChronoVaults</em>
            </h1>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl">
              ChronoVaults was created around a simple idea: some messages are too important to be
              left to chance.
            </p>
            <p className="mt-5 text-base text-white/60 leading-relaxed max-w-2xl">
              Life moves quickly. We think of things we want to say, moments we want to preserve,
              and words we hope someone will hear one day but the right moment is not always now.
            </p>
            <p className="mt-5 text-base text-white/60 leading-relaxed max-w-2xl">
              ChronoVaults gives people a secure way to create personal messages and schedule them
              for the future. Whether it is a birthday message, a milestone, a memory, a piece of
              advice, or something deeply personal, you can choose what you want to say and when
              you want it to arrive.
            </p>
            <p className="mt-5 text-base text-white/60 leading-relaxed max-w-2xl">
              Our aim is to make future messaging feel simple, private and meaningful.
            </p>
          </div>
        </div>
      </section>

      {/* MORE THAN A SCHEDULED MESSAGE */}
      <section className="border-t border-white/5 bg-vault-900/50">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
          <p className="label-mono text-terracotta">SECTION · 01</p>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-bone mt-6">
            More than a scheduled message
          </h2>
          <div className="mt-8 space-y-5 max-w-2xl">
            <p className="text-base text-white/60 leading-relaxed">
              A ChronoVault is not just an email sent later.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              It can be a video, a message, or a memory created today and held securely until the
              moment you choose.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              Recipients are asked to verify their identity before accessing a released message,
              helping ensure that personal content reaches the person it was intended for.
            </p>
          </div>
        </div>
      </section>

      {/* WHY WE BUILT IT */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
          <p className="label-mono text-terracotta">SECTION · 02</p>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-bone mt-6">
            Why we built it
          </h2>
          <div className="mt-8 space-y-5 max-w-2xl">
            <p className="text-base text-white/60 leading-relaxed">
              Most digital communication is designed for immediacy.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              ChronoVaults was built for the opposite.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              We wanted to create a place for the messages that deserve time, intention and meaning
              — the things you may want someone to receive months or years from now, rather than in
              the next few seconds.
            </p>
            <p className="text-base text-white/70 leading-relaxed">
              Because sometimes the value of a message is not only in what it says.
              <br />
              It is also in <em className="text-terracotta not-italic">when it arrives</em>.
            </p>
          </div>
        </div>
      </section>

      {/* BUILT WITH PRIVACY IN MIND */}
      <section className="border-t border-white/5 bg-vault-900/50">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
          <p className="label-mono text-terracotta">SECTION · 03</p>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-bone mt-6">
            Built with privacy in mind
          </h2>
          <div className="mt-8 space-y-5 max-w-2xl">
            <p className="text-base text-white/60 leading-relaxed">
              We know the messages people entrust to ChronoVaults can be deeply personal.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              That is why privacy, secure access and controlled delivery are central to how the
              platform is designed. We aim to collect only the information needed to operate the
              service and to protect access to messages through verification and secure delivery
              processes.
            </p>
          </div>
        </div>
      </section>

      {/* OUR PURPOSE */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
          <p className="label-mono text-terracotta">SECTION · 04</p>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-bone mt-6">
            Our purpose
          </h2>
          <div className="mt-8 space-y-5 max-w-2xl">
            <p className="text-base text-white/60 leading-relaxed">
              ChronoVaults exists to help people preserve the things they do not want to leave
              unsaid.
            </p>
            <div className="pl-6 border-l-2 border-terracotta/40 py-1 my-6">
              <p className="font-serif text-2xl text-bone leading-tight">Your words.</p>
              <p className="font-serif text-2xl text-bone leading-tight">Your moment.</p>
              <p className="font-serif text-2xl text-bone leading-tight">Your time.</p>
            </div>
            <p className="font-serif text-xl italic text-terracotta leading-relaxed">
              ChronoVaults: messages for the moments that matter.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT / LEGAL FOOTER */}
      <footer
        className="border-t border-white/10 bg-vault-950"
        data-testid="about-footer"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-3">
              <div
                className="flex items-center gap-2 text-sm text-white/60"
                data-testid="about-footer-location"
              >
                <MapPin className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span>ChronoVaults is based in the United Kingdom</span>
              </div>
              <a
                href="mailto:info@chronovaults.co.uk"
                className="flex items-center gap-2 text-sm text-bone/80 hover:text-terracotta transition-colors"
                data-testid="about-footer-email"
              >
                <Mail className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                <span>info@chronovaults.co.uk</span>
              </a>
            </div>

            <nav
              className="flex items-center gap-6 text-xs label-mono"
              data-testid="about-footer-legal"
              aria-label="Legal"
            >
              <Link
                to="/privacy"
                className="text-white/60 hover:text-bone transition-colors"
                data-testid="about-footer-privacy"
              >
                Privacy Policy
              </Link>
              <span className="text-white/20">·</span>
              <Link
                to="/terms"
                className="text-white/60 hover:text-bone transition-colors"
                data-testid="about-footer-terms"
              >
                Terms
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
